import React, { useCallback, useEffect, useMemo, useState } from "react";
import Navbar from "./Navbar";
import { Form, Button, Row, Col, Container, Table, Modal as RBModal, Card, Badge, Tab, Nav, ListGroup, FloatingLabel, Spinner, Alert } from "react-bootstrap";
import { BsPencil } from "react-icons/bs";
import { BsLock } from "react-icons/bs";
import { useLocation } from "react-router-dom";
import PageTitle from './components/PageTitle';
import VaccinationCardModal from './components/VaccinationCardModal';
import VaccinationCardViewModal from './components/VaccinationCardViewModal';
import { API_ENDPOINTS } from './config/api';
import { VACCINATION_CARD_ITEM_NAME, mapPromotionVaccineNameToCardVaccineId } from './constants/vaccinationCardCatalog';
import UserAvatar from './components/UserAvatar';

const EVIDENCE_OPTION_MAP = {
  licensesCertifications: ['BLS', 'ACLS', 'ATLS', 'Other'],
  onboardingRequirements: ['Onboarding Package', 'Required Document', 'Other'],
  trainingCertifications: ['HIPAA', 'OSHA', 'Other'],
  vaccinations: ['COVID-19', 'Flu', 'Other'],
  scholarlyActivity: ['Presentation', 'Conference', 'Submitted abstract or manuscript', 'Other']
};

const SCHOLARLY_ACTIVITY_TYPE_LABELS = {
  presentation: 'Presentation',
  conference: 'Conference',
  manuscript: 'Submitted abstract or manuscript'
};

const EVIDENCE_CATEGORY_LABELS = [
  ['licensesCertifications', 'Licenses/Certifications'],
  ['onboardingRequirements', 'Onboarding'],
  ['trainingCertifications', 'Training'],
  ['vaccinations', 'Vaccinations'],
  ['vaccinationCard', 'Vaccination card'],
  ['scholarlyActivity', 'Scholarly Activity']
];

function parseStructuredCardDetails(details) {
  if (details == null) return null;
  if (typeof details === 'object' && !Array.isArray(details)) return details;
  if (typeof details === 'string') {
    try {
      return JSON.parse(details);
    } catch {
      return null;
    }
  }
  return null;
}

function cardHasDoseForPromotionEvidenceId(cardDetails, promotionEvidenceId) {
  const d = parseStructuredCardDetails(cardDetails);
  if (!d?.vaccines || promotionEvidenceId == null) return false;
  const eid = Number(promotionEvidenceId);
  if (!Number.isFinite(eid)) return false;
  return Object.values(d.vaccines).some(
    (bucket) =>
      bucket &&
      Array.isArray(bucket.doses) &&
      bucket.doses.some((x) => Number(x?.promotionEvidenceId) === eid)
  );
}

const EVIDENCE_CATEGORY_DESCRIPTIONS = {
  licensesCertifications: 'Upload certification proof (BLS/ACLS/ATLS), include certificate type and expiration date.',
  onboardingRequirements: 'Submit assigned onboarding package/document completion proof for your selected PGY requirements.',
  trainingCertifications: 'Submit HIPAA/other training completion certificates, including custom training names when needed.',
  vaccinations: 'Upload vaccination proof and provide administration details such as date and location.',
  scholarlyActivity: 'Choose how you fulfilled the requirement (presentation, conference, or submitted work), add details, and upload supporting evidence.'
};

const ProfilePage = () => {
  const [user, setUser] = useState(null);
  const [form, setForm] = useState({});
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [isPersonalModalOpen, setIsPersonalModalOpen] = useState(false);
  const [isAddressModalOpen, setIsAddressModalOpen] = useState(false);
  const [isWorkModalOpen, setIsWorkModalOpen] = useState(false);
  const [isPasswordModalOpen, setIsPasswordModalOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [profilePic, setProfilePic] = useState(null);
  const [profilePicPreview, setProfilePicPreview] = useState(null);
  const [activeTab, setActiveTab] = useState('profile');
  const [inServiceExams, setInServiceExams] = useState([]);
  const [promotionEvidence, setPromotionEvidence] = useState([]);
  const [promotionActivities, setPromotionActivities] = useState([]);
  const [showActivityModal, setShowActivityModal] = useState(false);
  const [showVaccinationCardModal, setShowVaccinationCardModal] = useState(false);
  const [showVaccinationCardViewModal, setShowVaccinationCardViewModal] = useState(false);
  const [vaccinationCardSubmitting, setVaccinationCardSubmitting] = useState(false);
  const [selectedActivity, setSelectedActivity] = useState(null);
  const [evidenceForm, setEvidenceForm] = useState({
    category: 'licensesCertifications',
    enabled: false,
    selectedOption: 'BLS',
    otherOption: '',
    itemName: '',
    details: '',
    expirationDate: '',
    administrationDate: '',
    administrationLocation: '',
    completionDate: '',
    venueOrPublication: '',
    scholarlyActivityType: 'presentation',
    evidenceFile: null
  });
  const [profilePicDataUrl, setProfilePicDataUrl] = useState(null);
  const location = useLocation();
  // const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5000';

  // Function to load profile picture with authentication
  const loadProfilePicture = async (profilePicPath) => {
    if (!profilePicPath) return null;
    
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${API_ENDPOINTS.PROFILE_PICS}${profilePicPath}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      
      if (!response.ok) {
        console.warn('Failed to load profile picture:', response.status);
        return null;
      }
      
      const blob = await response.blob();
      return URL.createObjectURL(blob);
    } catch (error) {
      console.error('Error loading profile picture:', error);
      return null;
    }
  };

  // Function to generate fallback avatar
  const generateFallbackAvatar = (firstName, lastName) => {
    const firstInitial = firstName?.charAt(0) || 'U';
    const lastInitial = lastName?.charAt(0) || 'S';
    return `data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='120' height='120' viewBox='0 0 100 100'%3E%3Crect width='100' height='100' fill='%23e9ecef'/%3E%3Ctext x='50' y='50' text-anchor='middle' dy='.3em' font-size='40' fill='%23adb5bd'%3E${firstInitial}${lastInitial}%3C/text%3E%3C/svg%3E`;
  };

  // Function to format phone numbers
  const formatPhoneNumber = (phoneNumber) => {
    if (!phoneNumber) return '';
    
    // Remove all non-digits
    const cleaned = phoneNumber.replace(/\D/g, '');
    
    // Check if we have a valid 10-digit number
    if (cleaned.length === 10) {
      return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
    }
    
    // If not 10 digits, return as is
    return phoneNumber;
  };

  // Function to handle phone number input changes
  const handlePhoneChange = (value, field) => {
    // Remove all non-digits
    const cleaned = value.replace(/\D/g, '');
    
    // Limit to 10 digits
    const limited = cleaned.slice(0, 10);
    
    // Update the appropriate field
    if (field === 'phone') {
      setForm({ ...form, phone: limited });
    } else if (field === 'emergencyPhone') {
      setForm({ 
        ...form, 
        emergencyContact: { 
          ...form.emergencyContact, 
          phone: limited 
        } 
      });
    } else if (field === 'pagerNumber') {
      setForm({ ...form, pagerNumber: limited });
    }
  };

  useEffect(() => {
    const fetchUserProfile = async () => {
      try {
        const token = localStorage.getItem("token");
        console.log("Token:", token);

        if (!token) {
          throw new Error("No token found in localStorage");
        }

        const response = await fetch(`${API_ENDPOINTS.AUTH}/me`, {
          headers: { 
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json"
          },
        });

        console.log("Response status:", response.status);

        if (!response.ok) {
          const errorData = await response.json();
          console.error("Error response:", errorData);
          throw new Error(errorData.message || "Failed to fetch profile");
        }

        const data = await response.json();
        console.log("User data:", data);
        console.log("Emergency contact data:", data.emergencyContact);

        if (!data) {
          throw new Error("No user data received");
        }

        // Ensure emergency contact is properly parsed if it's a string
        if (data.emergencyContact) {
          if (typeof data.emergencyContact === 'string') {
            // Check if it's the "[object Object]" string
            if (data.emergencyContact === '[object Object]') {
              data.emergencyContact = { name: '', phone: '', relation: '' };
            } else {
              try {
                data.emergencyContact = JSON.parse(data.emergencyContact);
              } catch (e) {
                console.error('Error parsing emergency contact:', e);
                data.emergencyContact = { name: '', phone: '', relation: '' };
              }
            }
          } else if (typeof data.emergencyContact === 'object' && data.emergencyContact !== null) {
            // Already an object, ensure it has the required properties
            data.emergencyContact = {
              name: data.emergencyContact.name || '',
              phone: data.emergencyContact.phone || '',
              relation: data.emergencyContact.relation || ''
            };
          } else {
            // Fallback to empty object
            data.emergencyContact = { name: '', phone: '', relation: '' };
          }
        } else {
          // No emergency contact data, set to empty object
          data.emergencyContact = { name: '', phone: '', relation: '' };
        }

        setUser(data);
        setForm({
          firstName: data.firstName || "",
          lastName: data.lastName || "",
          phone: data.phone || "",
          address: data.address || { country: "United States" },
          gender: data.gender || "",
          ethnicity: data.ethnicity || "",
          pagerNumber: data.pagerNumber || "",
          maritalStatus: data.maritalStatus || "",
          emergencyContact: data.emergencyContact || { name: '', phone: '', relation: '' },
          department: data.department || "",
          positionTitle: data.positionTitle || "",
          startDate: data.startDate ? data.startDate.substring(0, 10) : "",
          endDate: data.endDate ? data.endDate.substring(0, 10) : "",
          licenseNumber: data.licenseNumber || "",
          npiNumber: data.npiNumber || "",
          boardCertified: data.boardCertified || false,
        });

        // Load profile picture if it exists
        if (data.profilePic) {
          const picUrl = await loadProfilePicture(data.profilePic);
          setProfilePicDataUrl(picUrl);
        }
      } catch (err) {
        console.error("Error fetching profile:", err);
        setError(err.message || "Failed to load profile information");
      } finally {
        setLoading(false);
      }
    };

    fetchUserProfile();
  }, []);

  useEffect(() => {
    if (location.search.includes('tab=documents') || location.hash === '#documents') {
      setActiveTab('documents');
    }
  }, [location]);

  useEffect(() => {
    // Fetch in-service exams for this user if resident
    const fetchInServiceExams = async () => {
      if (user && user.role === 'resident') {
        try {
          const token = localStorage.getItem("token");
          const res = await fetch(`${API_ENDPOINTS.PROMOTIONS}/inservice-exams/${user.id}`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          if (res.ok) {
            const data = await res.json();
            setInServiceExams(data.results || []);
          }
        } catch (err) {
          // ignore
        }
      }
    };
    fetchInServiceExams();
  }, [user]);

  useEffect(() => {
    const fetchPromotionEvidence = async () => {
      if (!user || user.role !== 'resident') return;
      try {
        const token = localStorage.getItem("token");
        const res = await fetch(`${API_ENDPOINTS.PROMOTIONS}/requirements-evidence/me`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (res.ok) {
          const data = await res.json();
          setPromotionEvidence(data.evidence || []);
        }
      } catch (err) {
        // ignore
      }
    };
    fetchPromotionEvidence();
  }, [user]);

  const fetchPromotionActivities = useCallback(async () => {
    if (!user || user.role !== 'resident') return;
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(`${API_ENDPOINTS.PROMOTIONS}/requirements/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setPromotionActivities(data.activities || []);
      }
    } catch (err) {
      // ignore
    }
  }, [user]);

  const latestStructuredVaccinationCardEvidence = useMemo(() => {
    const rows = (promotionEvidence || []).filter(
      (ev) => ev.category === 'vaccinationCard' && ev.itemName === VACCINATION_CARD_ITEM_NAME
    );
    if (rows.length === 0) return null;
    return rows.reduce((best, ev) => {
      const t = new Date(ev.updatedAt || ev.reviewedAt || 0).getTime();
      const bt = new Date(best.updatedAt || best.reviewedAt || 0).getTime();
      return t >= bt ? ev : best;
    });
  }, [promotionEvidence]);

  useEffect(() => {
    fetchPromotionActivities();
  }, [fetchPromotionActivities, promotionEvidence]);

  const parsePromotionEvidenceDetailsText = (details) => {
    if (!details) return '';
    try {
      const o = typeof details === 'string' ? JSON.parse(details) : details;
      if (o && typeof o === 'object' && o.schemaVersion != null && o.vaccines && typeof o.vaccines === 'object') {
        const n = Object.values(o.vaccines).reduce((acc, v) => acc + ((v && v.doses) ? v.doses.length : 0), 0);
        return `Vaccination card on file (${n} dose record${n === 1 ? '' : 's'})`;
      }
      if (o && typeof o === 'object' && o.scholarlyActivityType) {
        const typeLabel = SCHOLARLY_ACTIVITY_TYPE_LABELS[o.scholarlyActivityType] || o.scholarlyActivityType;
        const venue = o.venueOrPublication != null && String(o.venueOrPublication).trim()
          ? ` · ${String(o.venueOrPublication).trim()}`
          : '';
        const notes = o.info != null && String(o.info).trim() ? ` · ${String(o.info).trim()}` : '';
        return `${typeLabel}${venue}${notes}`;
      }
      if (o && typeof o === 'object' && o.info != null) return String(o.info);
      return typeof details === 'string' ? details : '';
    } catch {
      return typeof details === 'string' ? details : '';
    }
  };

  const parseEvidenceDetailsObject = (details) => {
    if (details == null) return {};
    try {
      return typeof details === 'string' ? JSON.parse(details) : details;
    } catch {
      return {};
    }
  };

  const openPromotionEvidenceFile = async (evidencePath) => {
    if (!evidencePath) return;
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_ENDPOINTS.PROFILE_PICS}${evidencePath}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      window.open(url, '_blank');
    } catch (err) {
      console.error('Error opening evidence file:', err);
      alert('Failed to open file. Please try again.');
    }
  };

  const submitPromotionEvidence = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem("token");
      const formData = new FormData();
      const resolvedItemName = evidenceForm.selectedOption === 'Other'
        ? evidenceForm.otherOption.trim()
        : evidenceForm.selectedOption;
      const detailsPayload = { info: evidenceForm.details || '' };
      if (evidenceForm.category === 'vaccinations') {
        if (evidenceForm.administrationDate) detailsPayload.administrationDate = evidenceForm.administrationDate;
        const loc = String(evidenceForm.administrationLocation || '').trim();
        if (loc) detailsPayload.administrationLocation = loc;
      }
      if (evidenceForm.category === 'trainingCertifications' && evidenceForm.completionDate) {
        detailsPayload.completionDate = evidenceForm.completionDate;
      }
      const venue = String(evidenceForm.venueOrPublication || '').trim();
      if (evidenceForm.category === 'scholarlyActivity') {
        detailsPayload.scholarlyActivityType = evidenceForm.scholarlyActivityType || 'presentation';
        if (venue) detailsPayload.venueOrPublication = venue;
      }
      formData.append('category', evidenceForm.category);
      formData.append('itemName', resolvedItemName || evidenceForm.itemName);
      formData.append('details', JSON.stringify(detailsPayload));
      if (evidenceForm.expirationDate) formData.append('expirationDate', evidenceForm.expirationDate);
      if (evidenceForm.evidenceFile) formData.append('evidenceFile', evidenceForm.evidenceFile);

      const res = await fetch(`${API_ENDPOINTS.PROMOTIONS}/requirements-evidence`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData
      });
      if (!res.ok) throw new Error('Failed to submit evidence');
      const data = await res.json();
      setPromotionEvidence((prev) => {
        const withoutSame = prev.filter((ev) => ev.id !== data.evidence?.id);
        return [data.evidence, ...withoutSame];
      });
      await fetchPromotionActivities();
      setShowActivityModal(false);
      setSelectedActivity(null);
      setEvidenceForm({
        category: 'licensesCertifications',
        enabled: false,
        selectedOption: 'BLS',
        otherOption: '',
        itemName: '',
        details: '',
        expirationDate: '',
        administrationDate: '',
        administrationLocation: '',
        completionDate: '',
        venueOrPublication: '',
        scholarlyActivityType: 'presentation',
        evidenceFile: null
      });
      alert('Evidence submitted for director review.');
    } catch (err) {
      alert(err.message || 'Failed to submit evidence');
    }
  };

  const openActivityModal = (activity) => {
    if (activity.category === 'vaccinationCard') {
      setSelectedActivity(activity);
      setShowVaccinationCardModal(true);
      return;
    }
    setSelectedActivity(activity);
    const ev = activity.evidence;
    const d = ev ? parseEvidenceDetailsObject(ev.details) : {};
    const priorDetails =
      activity.category === 'scholarlyActivity' && ev
        ? (d.info != null ? String(d.info) : '')
        : ev
          ? parsePromotionEvidenceDetailsText(ev.details)
          : '';
    const priorExp = ev && ev.expirationDate ? String(ev.expirationDate).substring(0, 10) : '';
    const adminDate = d.administrationDate ? String(d.administrationDate).substring(0, 10) : '';
    const compDate = d.completionDate ? String(d.completionDate).substring(0, 10) : '';
    setEvidenceForm((prev) => ({
      ...prev,
      enabled: true,
      category: activity.category,
      itemName: activity.title,
      selectedOption: activity.title,
      otherOption: '',
      details: priorDetails,
      expirationDate: priorExp,
      administrationDate: adminDate,
      administrationLocation: d.administrationLocation != null ? String(d.administrationLocation) : '',
      completionDate: compDate,
      venueOrPublication: d.venueOrPublication != null ? String(d.venueOrPublication) : '',
      scholarlyActivityType:
        d.scholarlyActivityType && SCHOLARLY_ACTIVITY_TYPE_LABELS[d.scholarlyActivityType]
          ? d.scholarlyActivityType
          : activity.title === 'Conference'
            ? 'conference'
            : activity.title === 'Submitted abstract or manuscript' || activity.title === 'Publication'
              ? 'manuscript'
              : activity.title === 'Presentation'
                ? 'presentation'
                : 'presentation',
      evidenceFile: null
    }));
    setShowActivityModal(true);
  };

  const submitVaccinationCard = async (detailsPayload, attachmentPayload = {}) => {
    setVaccinationCardSubmitting(true);
    try {
      const token = localStorage.getItem("token");
      const formData = new FormData();
      formData.append('category', 'vaccinationCard');
      formData.append('itemName', VACCINATION_CARD_ITEM_NAME);
      formData.append('details', JSON.stringify(detailsPayload));
      if (attachmentPayload.tbXrayFile) {
        formData.append('tbXrayFile', attachmentPayload.tbXrayFile);
      }
      const vaccineEvidenceEntries = Array.isArray(attachmentPayload.vaccineEvidenceEntries)
        ? attachmentPayload.vaccineEvidenceEntries
        : [];
      const uploadMeta = [];
      vaccineEvidenceEntries.forEach((entry, idx) => {
        if (!entry) return;
        const vaccineIds = Array.isArray(entry.vaccineIds) ? entry.vaccineIds.filter(Boolean) : [];
        if (entry.file) {
          formData.append(`vaccineCardEvidenceFile_${idx}`, entry.file);
          uploadMeta.push({ index: idx, vaccineIds });
        } else if (entry.path && entry.originalFileName) {
          uploadMeta.push({
            index: idx,
            vaccineIds,
            path: entry.path,
            originalFileName: entry.originalFileName
          });
        }
      });
      formData.append('vaccineCardEvidenceMeta', JSON.stringify(uploadMeta));
      const res = await fetch(`${API_ENDPOINTS.PROMOTIONS}/requirements-evidence`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.message || 'Failed to submit vaccination card');
      }
      const data = await res.json();
      setPromotionEvidence((prev) => {
        const withoutSame = prev.filter((ev) => ev.id !== data.evidence?.id);
        return [data.evidence, ...withoutSame];
      });
      await fetchPromotionActivities();
      setShowVaccinationCardModal(false);
      setSelectedActivity(null);
      alert('Vaccination card submitted for director review.');
    } catch (err) {
      alert(err.message || 'Failed to submit');
    } finally {
      setVaccinationCardSubmitting(false);
    }
  };

  const formatAddress = (address) => {
    if (!address || typeof address !== 'object') return "Not provided";
    
    const parts = [];
    if (address.street) parts.push(address.street);
    if (address.apt) parts.push(`Apt ${address.apt}`);
    if (address.city) parts.push(address.city);
    if (address.state) parts.push(address.state);
    if (address.zip) parts.push(address.zip);
    
    return parts.length > 0 ? parts.join(", ") : "Not provided";
  };

  const handleProfilePicChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setProfilePic(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setProfilePicPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSaveProfile = async (updatedFields) => {
    try {
      const token = localStorage.getItem("token");
      const formData = new FormData();
      // Add profile picture if it exists (only for personal info)
      if (profilePic && updatedFields && updatedFields.profilePic !== undefined) {
        formData.append('profilePic', profilePic);
      }
      // Merge updated fields into form
      const newForm = { ...form, ...updatedFields };
      setForm(newForm);
      // Add other form data
      Object.keys(newForm).forEach(key => {
        if (key === 'address' || key === 'emergencyContact') {
          formData.append(key, JSON.stringify(newForm[key]));
        } else {
          formData.append(key, newForm[key]);
        }
      });
      const res = await fetch(`${API_ENDPOINTS.AUTH}/me`, {
        method: "PUT",
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: formData,
      });
      if (!res.ok) {
        const errorData = await res.json();
        throw new Error(errorData.message || "Failed to update profile");
      }
              const updatedUser = await res.json();
        
        // Ensure emergency contact is properly parsed if it's a string
        if (updatedUser.emergencyContact) {
          if (typeof updatedUser.emergencyContact === 'string') {
            // Check if it's the "[object Object]" string
            if (updatedUser.emergencyContact === '[object Object]') {
              updatedUser.emergencyContact = { name: '', phone: '', relation: '' };
            } else {
              try {
                updatedUser.emergencyContact = JSON.parse(updatedUser.emergencyContact);
              } catch (e) {
                console.error('Error parsing emergency contact:', e);
                updatedUser.emergencyContact = { name: '', phone: '', relation: '' };
              }
            }
          } else if (typeof updatedUser.emergencyContact === 'object' && updatedUser.emergencyContact !== null) {
            // Already an object, ensure it has the required properties
            updatedUser.emergencyContact = {
              name: updatedUser.emergencyContact.name || '',
              phone: updatedUser.emergencyContact.phone || '',
              relation: updatedUser.emergencyContact.relation || ''
            };
          } else {
            // Fallback to empty object
            updatedUser.emergencyContact = { name: '', phone: '', relation: '' };
          }
        } else {
          // No emergency contact data, set to empty object
          updatedUser.emergencyContact = { name: '', phone: '', relation: '' };
        }
        
        // Ensure address is properly parsed if it's a string
        if (updatedUser.address) {
          if (typeof updatedUser.address === 'string') {
            try {
              updatedUser.address = JSON.parse(updatedUser.address);
            } catch (e) {
              console.error('Error parsing address:', e);
              updatedUser.address = { country: 'United States' };
            }
          } else if (typeof updatedUser.address === 'object' && updatedUser.address !== null) {
            // Already an object, ensure it has the required properties
            updatedUser.address = {
              street: updatedUser.address.street || '',
              apt: updatedUser.address.apt || '',
              city: updatedUser.address.city || '',
              state: updatedUser.address.state || '',
              zip: updatedUser.address.zip || '',
              country: updatedUser.address.country || 'United States'
            };
          } else {
            // Fallback to empty object with default country
            updatedUser.address = { country: 'United States' };
          }
        } else {
          // No address data, set to empty object with default country
          updatedUser.address = { country: 'United States' };
        }
        
        setUser(updatedUser);
      setProfilePic(null);
      setProfilePicPreview(null);
      
      // Reload profile picture if it was updated
      if (updatedUser.profilePic) {
        const picUrl = await loadProfilePicture(updatedUser.profilePic);
        setProfilePicDataUrl(picUrl);
      }
      
      // Store user data in localStorage
      localStorage.setItem("firstName", updatedUser.firstName);
      localStorage.setItem("lastName", updatedUser.lastName);
      localStorage.setItem("profilePic", updatedUser.profilePic || "");
      alert("Profile updated successfully!");
    } catch (err) {
      console.error("Error updating profile:", err);
      alert(err.message || "Failed to update profile");
    }
  };

  const handleChangePassword = async () => {
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(`${API_ENDPOINTS.AUTH}/change-password`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ currentPassword, newPassword }),
      });

      if (!res.ok) {
        throw new Error("Failed to change password");
      }

      setIsPasswordModalOpen(false);
      setCurrentPassword("");
      setNewPassword("");
      alert("Password changed successfully!");
    } catch (err) {
      console.error("Error changing password:", err);
      alert("Failed to change password");
    }
  };

  if (loading) {
    return (
      <div>
        <PageTitle title="Loading..." />
        <Navbar pageTitle="My Profile" />
        <div style={{ padding: "30px", textAlign: "center" }}>
          <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '200px' }}>
            <div className="text-center">
              <Spinner animation="border" role="status" className="mb-3 text-primary" >
                <span className="visually-hidden">Loading...</span>
              </Spinner>
              <p className="text-muted">Loading profile information...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div>
        <PageTitle title="Profile Error" />
        <Navbar pageTitle="My Profile" />
        <div style={{ padding: "30px", textAlign: "center", color: "red" }}>
          <p>{error}</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div>
        <PageTitle title="No Profile" />
        <Navbar pageTitle="My Profile" />
        <div style={{ padding: "30px", textAlign: "center" }}>
          <p>No profile information available</p>
        </div>
      </div>
    );
  }

  return (
    <div>
      <PageTitle title="My Profile" description="View and edit your profile information" />
      <Navbar pageTitle="My Profile" />
      <Container className="mt-4">          
        <Tab.Container activeKey={activeTab} onSelect={setActiveTab} className="mb-3">
        <Row>
          <Col lg={2}>
          <Card className="mb-4 px-0 py-2 border-0 shadow-sm custom-sticky-top" style={{ top: '5rem' }}>

            <Nav variant="pills" className="flex-column">
              <Nav.Item>
                <Nav.Link eventKey="menu" disabled>Menu</Nav.Link>
              </Nav.Item>
              <Nav.Item>
                <Nav.Link eventKey="profile">My Profile</Nav.Link>
              </Nav.Item>
              <Nav.Item>
                <Nav.Link eventKey="documents">Documents</Nav.Link>
              </Nav.Item>
            </Nav>
          </Card>
          </Col>
          <Col lg={10}>
            <Tab.Content>
              <Tab.Pane eventKey="profile">
 {/* Profile Summary Card - Modernized */}
        <Card className="mb-4 border-0 shadow-sm">
          <Card.Header className="d-flex flex-column border-bottom bg-gradient flex-md-row align-items-center justify-content-between gap-3 p-3 px-4">
            
            <div className="d-flex align-items-center gap-3">
            <UserAvatar user={user} size={64} />
              <div>
                <h5 className="mb-1 fw-semibold">{user?.firstName} {user?.lastName}</h5>
                <div className="d-flex gap-2 mb-1">
                  
                  {user?.role === 'resident' && user?.year && (
                    <Badge bg="success">PGY-{user?.year}</Badge>
                  )}
                  <span className="text-muted">{user?.role}</span>
                </div>
              </div>
            </div>
            <div className="d-flex gap-2 mt-3 mt-md-0">
              <Button variant="light" className="d-flex align-items-center shadow-sm" style={{ borderRadius: 8, transition: 'box-shadow 0.2s' }} onClick={() => setIsPersonalModalOpen(true)} onMouseOver={e => e.currentTarget.style.boxShadow = '0 2px 8px #d1d5db'} onMouseOut={e => e.currentTarget.style.boxShadow = 'none'}>
                <i className="bi bi-pencil me-1" /> Edit
              </Button>
              <Button variant="outline-primary" size="sm" className="d-flex align-items-center shadow-sm" style={{ borderRadius: 8, transition: 'box-shadow 0.2s' }} onClick={() => setIsPasswordModalOpen(true)} onMouseOver={e => e.currentTarget.style.boxShadow = '0 2px 8px #d1d5db'} onMouseOut={e => e.currentTarget.style.boxShadow = 'none'}>
                <BsLock className="me-1" /> Change Password
              </Button>
            </div>
          </Card.Header>
          <Card.Body className="p-4">
          <Row> 
            <Col md={6}>
                  <div className="d-flex justify-content-between align-items-center">
            <h6 className="fw-semibold mb-0">Personal information</h6>
            <Button variant="light-solid" size="sm" onClick={() => setIsPersonalModalOpen(true)}>
             Edit 
            </Button>
          </div>
          <Table borderless className="small">
            <tbody>
              <tr>
                <td className="fw-semibold">Name</td>
                <td className="text-start">{[user?.firstName, user?.lastName].filter(Boolean).join(" ")}</td>
              </tr>
              <tr>
                <td className="fw-semibold">Email Address</td>
                <td className="text-start">{user?.email}</td>
              </tr>
              <tr>
                <td className="fw-semibold">Role</td>
                <td className="text-start">{user?.role}</td>
              </tr>
              <tr>
                <td className="fw-semibold">PGY Level</td>
                <td className="text-start">{user?.role === 'resident' && user?.year ? `PGY-${user?.year}` : 'N/A'}</td>
              </tr>
              <tr>
                <td className="fw-semibold">Gender</td>
                <td className="text-start">{user?.gender}</td>
              </tr>
              <tr>
                <td className="fw-semibold">Ethnicity</td>
                <td className="text-start">{user?.ethnicity}</td>
              </tr>
              <tr>
                <td className="fw-semibold">Marital Status</td>
                <td className="text-start">{user?.maritalStatus}</td>
              </tr>
            </tbody>
          </Table>
          </Col>
          <Col md={6}>
          <div className="d-flex justify-content-between align-items-center">
            <h6 className="fw-semibold mb-0">Contact information</h6>
            <Button variant="light-solid" size="sm" onClick={() => setIsAddressModalOpen(true)}>
             Edit 
            </Button>
          </div>
          <Table borderless className="small">
            <tbody>
              <tr>
                <td className="fw-semibold">Address</td>
                <td className="text-start">
                {user?.address ? (
                  <>
                    {user.address.street && <span>{user.address.street}{user.address.apt ? `, Apt/Suite ${user.address.apt}` : ''}</span>}<br />
                    {(user.address.city || user.address.state || user.address.zip) && (
                      <span>
                        {user.address.city ? user.address.city : ''}
                        {user.address.city && user.address.state ? ', ' : ''}
                        {user.address.state ? user.address.state : ''}
                        {(user.address.city || user.address.state) && user.address.zip ? ' ' : ''}
                        {user.address.zip ? user.address.zip : ''}
                      </span>
                    )}<br />
                    {user.address.country && <span>{user.address.country}</span>}
                  </>
                ) : (
                  <span>Not provided</span>
                )}
              </td>
              </tr>
              <tr>
                <td className="fw-semibold">Phone</td>
                <td className="text-start">{user?.phone ? formatPhoneNumber(user.phone) : 'Not provided'}</td>
              </tr>
              <tr>
                <td className="fw-semibold">Pager Number</td>
                <td className="text-start">{user?.pagerNumber ? formatPhoneNumber(user.pagerNumber) : 'Not provided'}</td>
              </tr>
              <tr>
                <td className="fw-semibold">Emergency Contact</td>
                <td className="text-start">
                  {user?.emergencyContact
                    ? (
                      <>
                        {user.emergencyContact.name || 'Not provided'}
                        {user.emergencyContact.relation && ` (${user.emergencyContact.relation})`}
                        {user.emergencyContact.phone
                          ? `, ${formatPhoneNumber(user.emergencyContact.phone)}`
                          : ', Not provided'}
                      </>
                    )
                    : 'Not provided'}
                </td>
              </tr>
            </tbody>
          </Table>
          </Col>
          </Row>
          </Card.Body>

        </Card>
        <div className="mb-4" />

        <div className="mb-4" nmn />

        {/* Work Information Card - Only for Attendings */}
        {user?.role === 'attending' || user?.role === 'director' && (
          <Card className="mb-4 p-4 border-0 shadow-sm">
            <div className="d-flex justify-content-between align-items-center mb-2">
              <strong>Work Information</strong>
              <Button variant="light" size="sm" onClick={() => setIsWorkModalOpen(true)}>
                <i className="bi bi-pencil me-1" />Edit 
              </Button>
          </div>
            <Row>
              <Col xs={6}><div className="text-muted">Department</div><div>{user?.department}</div></Col>
              <Col xs={6}><div className="text-muted">Position Title</div><div>{user?.positionTitle}</div></Col>
              <Col xs={6}><div className="text-muted">License Number</div><div>{user?.licenseNumber}</div></Col>
              <Col xs={6}><div className="text-muted">NPI Number</div><div>{user?.npiNumber}</div></Col>
              <Col xs={6}><div className="text-muted">Board Certified</div><div>{user?.boardCertified ? "Yes" : "No"}</div></Col>
              <Col xs={6}><div className="text-muted">Start Date</div><div>{user?.startDate ? user.startDate.substring(0, 10) : ""}</div></Col>
              <Col xs={6}><div className="text-muted">End Date</div><div>{user?.endDate ? user.endDate.substring(0, 10) : ""}</div></Col>
            </Row>
          </Card>
        )}
        <div className="mb-4" />
              </Tab.Pane>
              <Tab.Pane eventKey="documents">
                <Card className="mb-4 p-4 border-0 shadow-sm" style={{ borderRadius: 16 }}>
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <strong>Documents</strong>
                  </div>
                  {/* In-Service Exam PDFs */}
                  {user?.role === 'resident' && inServiceExams.length > 0 && (
                    <div className="mb-3">
                      <h6>In-Service Exam PDFs</h6>
                      <ListGroup>
                        {inServiceExams.filter(exam => exam.pdfFile).map((exam, idx) => {
                          const academicYearFormatted = exam.academicYear ? `${exam.academicYear}-${parseInt(exam.academicYear) + 1}` : 'N/A';
                          const pdfUrl = exam.pdfFile.startsWith('http') ? exam.pdfFile : 
                            exam.pdfFile.startsWith('/uploads/') ? `${API_ENDPOINTS.PROFILE_PICS}${exam.pdfFile}` : 
                            `${API_ENDPOINTS.UPLOADS}/${exam.pdfFile}`;
                          
                          const handleViewPDF = async (e) => {
                            e.preventDefault();
                            try {
                              const token = localStorage.getItem('token');
                              const response = await fetch(pdfUrl, {
                                headers: {
                                  'Authorization': `Bearer ${token}`,
                                  'Content-Type': 'application/pdf'
                                }
                              });
                              
                              if (!response.ok) {
                                throw new Error(`HTTP error! status: ${response.status}`);
                              }
                              
                              const blob = await response.blob();
                              const url = window.URL.createObjectURL(blob);
                              window.open(url, '_blank');
                            } catch (error) {
                              console.error('Error opening PDF:', error);
                              alert('Failed to open PDF. Please try again.');
                            }
                          };
                          
                          const handleDownloadPDF = async (e) => {
                            e.preventDefault();
                            try {
                              const token = localStorage.getItem('token');
                              const response = await fetch(pdfUrl, {
                                headers: {
                                  'Authorization': `Bearer ${token}`,
                                  'Content-Type': 'application/pdf'
                                }
                              });
                              
                              if (!response.ok) {
                                throw new Error(`HTTP error! status: ${response.status}`);
                              }
                              
                              const blob = await response.blob();
                              const url = window.URL.createObjectURL(blob);
                              const link = document.createElement('a');
                              link.href = url;
                              link.download = `exam-${exam.examDate || idx + 1}.pdf`;
                              document.body.appendChild(link);
                              link.click();
                              document.body.removeChild(link);
                              window.URL.revokeObjectURL(url);
                            } catch (error) {
                              console.error('Error downloading PDF:', error);
                              alert('Failed to download PDF. Please try again.');
                            }
                          };
                          
                          return (
                            <ListGroup.Item key={idx} className="d-flex align-items-center justify-content-between">
                              <div>
                                <a href="#" onClick={handleViewPDF} style={{ textDecoration: 'none', color: '#007bff' }}>
                                  {exam.examDate ? `Exam on ${exam.examDate}` : `Exam #${idx + 1}`} (PDF)
                                </a>
                                <div style={{ fontSize: '0.9em', color: '#666' }}>
                                  Academic Year: {academicYearFormatted} | Uploaded: {exam.createdAt ? new Date(exam.createdAt).toLocaleDateString() : 'N/A'}
                                </div>
                              </div>
                              <a href="#" onClick={handleDownloadPDF} title="Download PDF" style={{ marginLeft: 12, color: '#007bff' }}>
                                <i className="bi bi-download" style={{ fontSize: '1.2em' }}></i>
                              </a>
                            </ListGroup.Item>
                          );
                        })}
                      </ListGroup>
                    </div>
                  )}
                  {user?.role === 'resident' && (
                    <>
                      <hr />
                      <h6>Assigned Promotion Activities</h6>
                      {promotionActivities.length === 0 ? (
                        <div className="text-muted mb-3">No assigned promotion activities.</div>
                      ) : (
                        <ListGroup className="mb-3">
                          {promotionActivities.map((activity, idx) => {
                            const ev = activity.evidence;
                            const detailsText = ev ? parsePromotionEvidenceDetailsText(ev.details) : '';
                            const submittedOrApproved = activity.status === 'submitted' || activity.status === 'approved';
                            const showSubmissionSummary =
                              ev &&
                              (submittedOrApproved ||
                                activity.status === 'rejected' ||
                                (activity.status === 'pending' && activity.evidenceInvalidated));
                            const showCompleteButton = activity.status === 'pending' || activity.status === 'rejected';
                            const showImmunizationRecordFromVaccination =
                              activity.category === 'vaccinations' &&
                              activity.status === 'approved' &&
                              !!mapPromotionVaccineNameToCardVaccineId(activity.title) &&
                              !!latestStructuredVaccinationCardEvidence &&
                              !!ev?.id &&
                              cardHasDoseForPromotionEvidenceId(
                                latestStructuredVaccinationCardEvidence.details,
                                ev.id
                              );
                            const showEvidenceDetailsColumn =
                              !!detailsText ||
                              (activity.category === 'vaccinationCard' && !!ev?.details) ||
                              showImmunizationRecordFromVaccination;
                            return (
                              <ListGroup.Item key={`${activity.category}-${activity.title}-${idx}`} className="d-flex flex-column">
                                <div className="d-flex justify-content-md-between flex-column flex-md-row align-items-start align-items-md-center">
                                <div className="flex-grow-1 me-2 mb-md-0 mb-2">
                                  <div className="fw-semibold">{activity.title}</div>
                                  <div className="small text-muted">{activity.description || `Complete required evidence for ${activity.categoryTitle}.`}</div>
                                  {activity.isFromPriorYear && activity.academicYear ? (
                                    <div className={`small ${activity.status === 'approved' ? 'text-muted' : 'text-warning'}`}>
                                      From prior academic year {activity.academicYear}-{activity.academicYear + 1}
                                      {activity.status === 'approved' ? '' : ' (uncompleted)'}
                                    </div>
                                  ) : activity.academicYear && !activity.isFromPriorYear ? (
                                    <div className="small text-muted">
                                      Academic year {activity.academicYear}-{activity.academicYear + 1}
                                    </div>
                                  ) : null}
                                </div>
                              

                                <div className="d-flex flex-row align-items-center justify-content-end gap-2">
                                  <Badge bg={activity.status === 'approved' ? 'success' : activity.status === 'rejected' ? 'danger' : activity.status === 'submitted' ? 'warning' : 'secondary'}>
                                    {activity.status}
                                  </Badge>
                                  {showCompleteButton ? (
                                    <Button
                                      size="sm"
                                      variant="primary"
                                      className="text-nowrap"
                                      onClick={() => openActivityModal(activity)}
                                    >
                                      {activity.status === 'rejected' ? 'Resubmit' : 'Complete Activity'}
                                    </Button>
                                  ) : null}
                                </div>
                                </div>
                                <div>
                                 {showSubmissionSummary && (
                                    <div className="d-flex flex-column flex-md-row align-items-start align-items-md-center justify-content-between mt-2 p-2 px-3 rounded bg-light w-100">
                                     
                                      
                                      {showEvidenceDetailsColumn ? (
                                      <div className="d-flex flex-column align-items-start">
                                        {activity.category === 'vaccinationCard' && ev?.details ? (
                                       <Button
                                          variant="link"
                                          size="lg"
                                          className="p-0 text-decoration-none"
                                          onClick={() => {
                                            setSelectedActivity(activity);
                                            setShowVaccinationCardViewModal(true);
                                          }}
                                        >
                                          View structured immunization record
                                        </Button>
                                        ) : null}
                                        {showImmunizationRecordFromVaccination ? (
                                          <Button
                                            variant="link"
                                            size="sm"
                                            className="p-0 text-decoration-none mb-1"
                                            onClick={() => {
                                              setSelectedActivity({
                                                category: 'vaccinationCard',
                                                title: activity.title,
                                                evidence: latestStructuredVaccinationCardEvidence
                                              });
                                              setShowVaccinationCardViewModal(true);
                                            }}
                                          >
                                            View immunization record
                                          </Button>
                                        ) : null}
                                      {detailsText ? <div className="mb-1 small">{detailsText}</div> : null}
                                      </div>
                                      ) : null}
                                      
                                    <div className="">
                                      {ev.evidencePath ? (
                                        <Button
                                          variant="link"
                                          size="sm"
                                          className="p-0 text-decoration-none"
                                          onClick={() => openPromotionEvidenceFile(ev.evidencePath)}
                                        >
                                          {ev.originalFileName || 'View uploaded file'}
                                        </Button>
                                      ) : null}
                                    
                                      {ev.updatedAt ? (
                                        <div className="text-muted small">
                                          <b>Submitted:</b> {new Date(ev.updatedAt).toLocaleString()}
                                        </div>
                                      ) : null}  
                                    </div>
                                      
                                      {ev.expirationDate ? (
                                        <div className="d-flex flex-md-column flex-md-row align-items-start align-items-md-end justify-content-md-end justify-content-start text-muted gap-md-0 gap-2"><span className="text-muted small mb-0">Expiration:</span> <b>{new Date(ev.expirationDate).toLocaleDateString()}</b></div>
                                      ) : null}
                                    </div>
                                  )}
                                  {activity.status === 'rejected' && ev?.reviewNotes ? (
                                    <Alert variant="info" className="mt-2 mb-0 py-2 px-3 small">
                                      <span className="fw-semibold">Rejection reason:</span> {ev.reviewNotes}
                                    </Alert>
                                  ) : null}
                                  {Array.isArray(activity.priorCompletions) && activity.priorCompletions.length > 0 ? (
                                    <div className="mt-2 ms-1 ps-3 border-start">
                                      <div className="small text-muted mb-2">Prior academic year completions</div>
                                      <ListGroup variant="flush" className="rounded border">
                                        {activity.priorCompletions.map((prior, priorIdx) => {
                                          const priorEv = prior.evidence;
                                          const priorDetailsText = priorEv ? parsePromotionEvidenceDetailsText(priorEv.details) : '';
                                          return (
                                            <ListGroup.Item
                                              key={`prior-${prior.category}-${prior.title}-${prior.academicYear}-${priorIdx}`}
                                              className="py-2 px-3"
                                            >
                                              <div className="d-flex justify-content-between align-items-start gap-2">
                                                <div>
                                                  <div className="small fw-semibold">
                                                    {prior.academicYear}-{prior.academicYear + 1}
                                                    {prior.pgyLevel ? ` · PGY-${prior.pgyLevel}` : ''}
                                                  </div>
                                                  {priorDetailsText ? (
                                                    <div className="small text-muted">{priorDetailsText}</div>
                                                  ) : null}
                                                  {priorEv?.updatedAt ? (
                                                    <div className="small text-muted">
                                                      Completed {new Date(priorEv.updatedAt).toLocaleDateString()}
                                                    </div>
                                                  ) : null}
                                                </div>
                                                <div className="d-flex flex-column align-items-end gap-1">
                                                  <Badge bg="success">{prior.status}</Badge>
                                                  {prior.category === 'vaccinationCard' && priorEv?.details ? (
                                                    <Button
                                                      variant="link"
                                                      size="sm"
                                                      className="p-0 text-decoration-none"
                                                      onClick={() => {
                                                        setSelectedActivity(prior);
                                                        setShowVaccinationCardViewModal(true);
                                                      }}
                                                    >
                                                      View record
                                                    </Button>
                                                  ) : null}
                                                  {priorEv?.evidencePath ? (
                                                    <Button
                                                      variant="link"
                                                      size="sm"
                                                      className="p-0 text-decoration-none"
                                                      onClick={() => openPromotionEvidenceFile(priorEv.evidencePath)}
                                                    >
                                                      {priorEv.originalFileName || 'View file'}
                                                    </Button>
                                                  ) : null}
                                                </div>
                                              </div>
                                            </ListGroup.Item>
                                          );
                                        })}
                                      </ListGroup>
                                    </div>
                                  ) : null}
                                </div>
                              </ListGroup.Item>
                            );
                          })}
                        </ListGroup>
                      )}
                      
                    </>
                  )}
                </Card>
              </Tab.Pane>
            </Tab.Content>         
          </Col>      

        </Row> </Tab.Container>
 

        <RBModal show={showActivityModal} onHide={() => setShowActivityModal(false)} centered>
          <RBModal.Header closeButton className="border-bottom-0 px-4">
            <RBModal.Title className="w-100">
              <div className="d-flex flex-column justify-content-start align-items-start">
                <span>Complete Activity</span>
                <div className="text-hl"></div>
              </div>
            </RBModal.Title>
          </RBModal.Header>
          <RBModal.Body className="px-4">
            {selectedActivity && (
              <Form id="promotion-evidence-activity-form" onSubmit={submitPromotionEvidence}>
                <div className="fw-semibold">{selectedActivity.title}</div>
                <div className="text-muted mb-3">
                  {selectedActivity.description || `Provide required evidence for ${selectedActivity.categoryTitle}.`}
                </div>
              
                {(selectedActivity.category === 'licensesCertifications') && (
                  <Form.Group className="mb-2">
                    <FloatingLabel controlId="floatingInput" label="Expiration Date">
                    <Form.Control
                      type="date"
                      value={evidenceForm.expirationDate}
                      onChange={(e) => setEvidenceForm({ ...evidenceForm, expirationDate: e.target.value })}
                    />
                    </FloatingLabel>
                  </Form.Group>
                )}
                {(selectedActivity.category === 'vaccinations') && (
                  <>
                  <Row className="g-2">
                    <Col xs={6}>
                    <Form.Group className="mb-2">
                      <FloatingLabel controlId="floatingInput" label="Administration date">
                      <Form.Control
                        type="date"
                        value={evidenceForm.administrationDate}
                        onChange={(e) => setEvidenceForm({ ...evidenceForm, administrationDate: e.target.value })}
                      />
                      </FloatingLabel>
                    </Form.Group>
                    </Col>
                    <Col xs={6}>
                    <Form.Group className="mb-2">
                      <FloatingLabel controlId="floatingInput" label="Location">
                      <Form.Control
                        placeholder="Clinic, pharmacy, employer site, etc."
                        value={evidenceForm.administrationLocation}
                        onChange={(e) => setEvidenceForm({ ...evidenceForm, administrationLocation: e.target.value })}
                      />
                      </FloatingLabel>
                    </Form.Group>
                    </Col>
                    </Row>
                  </>
                )}
                {(selectedActivity.category === 'trainingCertifications') && (
                  <Form.Group className="mb-2">
                    <FloatingLabel controlId="floatingInput" label="Completion date">
                    <Form.Control
                      type="date"
                      value={evidenceForm.completionDate}
                      onChange={(e) => setEvidenceForm({ ...evidenceForm, completionDate: e.target.value })}
                    />
                    </FloatingLabel>
                  </Form.Group>
                )}
                {(selectedActivity.category === 'scholarlyActivity') && (
                  <>
                    <Form.Group className="mb-2 d-flex flex-row align-items-center justify-content-between gap-2">
                      <FloatingLabel controlId="floatingInput" label="Type of activity">
                      <Form.Select
                        value={evidenceForm.scholarlyActivityType}
                        onChange={(e) =>
                          setEvidenceForm({ ...evidenceForm, scholarlyActivityType: e.target.value })
                        }
                      >
                        <option value="presentation">Presentation</option>
                        <option value="conference">Conference</option>
                        <option value="manuscript">Submitted abstract</option>
                        <option value="manuscript">Manuscript</option>
                      </Form.Select>
                      </FloatingLabel>
                      <FloatingLabel controlId="scholarly-venue" label="Venue, journal, conference, or title">
                        <Form.Control
                          placeholder="e.g. national meeting name, journal, poster title"
                          value={evidenceForm.venueOrPublication}
                          onChange={(e) => setEvidenceForm({ ...evidenceForm, venueOrPublication: e.target.value })}
                        />
                      </FloatingLabel>
                    </Form.Group>
                  </>
                )}  
                <Form.Group className="mb-2">
                  <FloatingLabel controlId="floatingTextarea" label="Additional details">
                  <Form.Control
                    as="textarea"
                    rows={3}
                    placeholder="Enter required details"
                    value={evidenceForm.details}
                    onChange={(e) => setEvidenceForm({ ...evidenceForm, details: e.target.value })}
                  />
                  </FloatingLabel>
                </Form.Group>
                <Form.Group className="mb-3 mt-2">
                  <Form.Label>Upload certificate, document, or file</Form.Label>
                  <Form.Control
                    type="file"
                    onChange={(e) => setEvidenceForm({ ...evidenceForm, evidenceFile: e.target.files[0] })}
                    required
                  />
                </Form.Group>
              </Form>
            )}
          </RBModal.Body>
          <RBModal.Footer className="border-top-0 px-4">
            <div className="d-flex justify-content-end gap-2 w-100">
              <Button variant="light" size="sm" type="button" onClick={() => setShowActivityModal(false)}>
                Cancel
              </Button>
              <Button
                type="submit"
                form="promotion-evidence-activity-form"
                variant="primary"
                size="sm"
                disabled={!selectedActivity}
              >
                Submit
              </Button>
            </div>
          </RBModal.Footer>
        </RBModal>

        <VaccinationCardModal
          show={showVaccinationCardModal}
          onHide={() => {
            setShowVaccinationCardModal(false);
            setSelectedActivity(null);
          }}
          initialDetails={selectedActivity?.evidence?.details}
          onSubmit={submitVaccinationCard}
          submitting={vaccinationCardSubmitting}
        />
        <VaccinationCardViewModal
          show={showVaccinationCardViewModal}
          onHide={() => {
            setShowVaccinationCardViewModal(false);
            setSelectedActivity(null);
          }}
          details={selectedActivity?.evidence?.details}
          residentName={user ? `${user.firstName || ''} ${user.lastName || ''}`.trim() : ''}
        />

        {/* Personal Information Modal */}
        <RBModal show={isPersonalModalOpen} onHide={() => setIsPersonalModalOpen(false)} centered>
          <RBModal.Header closeButton className="border-bottom-0 px-4">
            <RBModal.Title className="w-100"> 
              <div className="d-flex flex-column justify-content-start align-items-start">
                <span>Edit Personal Information</span>
                <div className="text-hl"></div>
              </div>
            </RBModal.Title>
          </RBModal.Header>
          <RBModal.Body className="px-4">
            <Form>
              <div className="text-center mb-2">
                <div style={{ width: "80px", height: "80px", borderRadius: "50%", overflow: "hidden", margin: "0 auto", border: "2px solid #ddd" }}>
              <img 
                src={profilePicPreview || profilePicDataUrl || generateFallbackAvatar(user?.firstName, user?.lastName)} 
                alt="Profile" 
                style={{ width: "100%", height: "100%", objectFit: "cover" }}
                onError={(e) => {
                  e.target.src = generateFallbackAvatar(user?.firstName, user?.lastName);
                }}
              />
            </div>
                <Form.Group controlId="profilePicUpload" className="mt-2">
                  <Form.Control  type="file" accept="image/*" onChange={handleProfilePicChange} />
                </Form.Group>
          </div>
              <Row>
                <Col><Form.Group className="mb-2">
                  <FloatingLabel controlId="floatingInput" label="First Name">
                    <Form.Control placeholder="First Name" value={form.firstName || ""} onChange={e => setForm({ ...form, firstName: e.target.value })} required />
                  </FloatingLabel>
                </Form.Group></Col>
                <Col><Form.Group className="mb-2">
                  <FloatingLabel controlId="floatingInput" label="Last Name">
                    <Form.Control placeholder="Last Name" value={form.lastName || ""} onChange={e => setForm({ ...form, lastName: e.target.value })} required />
                  </FloatingLabel>
                </Form.Group></Col>
              </Row>
              <Row>
          
                <Col><Form.Group className="mb-2">
                  <FloatingLabel controlId="floatingInput" label="Gender">
                    <Form.Select value={form.gender || ''} onChange={e => setForm({ ...form, gender: e.target.value })}>
                    <option value="">Gender...</option>
                    <option value="Male">Male</option>
                    <option value="Female">Female</option>
                    <option value="Other">Other</option>
                    <option value="Prefer not to say">Prefer not to say</option>
                  </Form.Select>
                  </FloatingLabel>
                </Form.Group></Col>
                <Col><Form.Group className="mb-2">
                   <FloatingLabel controlId="floatingInput" label="Marital Status">
                     <Form.Select value={form.maritalStatus || ''} onChange={e => setForm({ ...form, maritalStatus: e.target.value })}>
                       <option value="">Select Marital Status...</option>
                       <option value="Single">Single</option>
                       <option value="Married">Married</option>
                       <option value="Divorced">Divorced</option>
                       <option value="Widowed">Widowed</option>
                       <option value="Separated">Separated</option>
                       <option value="Domestic Partnership">Domestic Partnership</option>
                       <option value="Civil Union">Civil Union</option>
                       <option value="Engaged">Engaged</option>
                       <option value="Prefer not to say">Prefer not to say</option>
                     </Form.Select>
                   </FloatingLabel>
                </Form.Group></Col>    
                <Col><Form.Group className="mb-2">
                   <FloatingLabel controlId="floatingInput" label="Ethnicity">
                     <Form.Select value={form.ethnicity || ''} onChange={e => setForm({ ...form, ethnicity: e.target.value })}>
                       <option value="">Select Ethnicity...</option>
                       <option value="White">White</option>
                       <option value="Black or African American">Black or African American</option>
                       <option value="Hispanic or Latino">Hispanic or Latino</option>
                       <option value="Asian">Asian</option>
                       <option value="Native American or Alaska Native">Native American or Alaska Native</option>
                       <option value="Native Hawaiian or Pacific Islander">Native Hawaiian or Pacific Islander</option>
                       <option value="Middle Eastern or North African">Middle Eastern or North African</option>
                       <option value="Multiracial">Multiracial</option>
                       <option value="Other">Other</option>
                       <option value="Prefer not to say">Prefer not to say</option>
                     </Form.Select>
                   </FloatingLabel>
                 </Form.Group></Col>
              </Row>
              <Row>
              <Col xs={12}>
                  <span className="small mb-2 text-muted">Contact information</span>
                </Col>
                 <Col><Form.Group className="mb-2">
                   <FloatingLabel controlId="floatingInput" label="Pager Number">
                     <Form.Control 
                       placeholder="Pager Number" 
                       value={form.pagerNumber ? formatPhoneNumber(form.pagerNumber) : ""} 
                       onChange={e => handlePhoneChange(e.target.value, 'pagerNumber')} 
                     />
                   </FloatingLabel>
                 </Form.Group></Col>

               <Col><Form.Group className="mb-2">
                  <FloatingLabel controlId="floatingInput" label="Phone">
                    <Form.Control 
                      placeholder="Phone" 
                      value={form.phone ? formatPhoneNumber(form.phone) : ""} 
                      onChange={e => handlePhoneChange(e.target.value, 'phone')} 
                    />
                  </FloatingLabel>
                </Form.Group></Col>
                 
              </Row>
              <Row>
                <Col xs={12}>
                  <span className="small mb-2 text-muted">Emergency Contact</span>
                </Col>
              </Row>
              <Row>
                <Col><Form.Group className="mb-2">
                  <FloatingLabel controlId="floatingInput" label="Emergency Contact Name">
                    <Form.Control placeholder="Emergency Contact Name" value={form.emergencyContact?.name || ""} onChange={e => setForm({ ...form, emergencyContact: { ...form.emergencyContact, name: e.target.value } })} />
                  </FloatingLabel>
                </Form.Group></Col>
                <Col><Form.Group className="mb-2">
                  <FloatingLabel controlId="floatingInput" label="Emergency Contact Phone">
                    <Form.Control 
                      placeholder="Emergency Contact Phone" 
                      value={form.emergencyContact?.phone ? formatPhoneNumber(form.emergencyContact.phone) : ""} 
                      onChange={e => handlePhoneChange(e.target.value, 'emergencyPhone')} 
                    />
                  </FloatingLabel>
                </Form.Group></Col>
              </Row>
              <Row>
                <Col><Form.Group>
                  <FloatingLabel controlId="floatingInput" label="Emergency Contact Relation">
                    <Form.Control placeholder="Emergency Contact Relation" value={form.emergencyContact?.relation || ""} onChange={e => setForm({ ...form, emergencyContact: { ...form.emergencyContact, relation: e.target.value } })} />
                  </FloatingLabel>
                </Form.Group></Col>
                </Row>
            </Form>
          </RBModal.Body>
          <RBModal.Footer className="px-4 border-top-0">
            <Button variant="light" onClick={() => setIsPersonalModalOpen(false)}>
              Cancel
            </Button>
            <Button variant="primary" onClick={() => { setIsPersonalModalOpen(false); handleSaveProfile({
              firstName: form.firstName,
              lastName: form.lastName,
              phone: form.phone,
              gender: form.gender,
                             ethnicity: form.ethnicity,
               pagerNumber: form.pagerNumber,
              maritalStatus: form.maritalStatus,
              emergencyContact: form.emergencyContact,
              profilePic: profilePic
            }); }}>
              Save Changes
            </Button>
          </RBModal.Footer>
        </RBModal>

        {/* Address Modal */}
        <RBModal show={isAddressModalOpen} onHide={() => setIsAddressModalOpen(false)} centered>
          <RBModal.Header closeButton className="border-bottom-0 px-4">
            <RBModal.Title className="w-100"> 
              <div className="d-flex flex-column justify-content-start align-items-start">
                <span>Edit Address</span>
                <div className="text-hl"></div>
              </div>
            </RBModal.Title>
          </RBModal.Header>
          <RBModal.Body className="px-4">
            <Form>
              <Row className="g-2">
                <Col xs={12} className="mb-3"><Form.Group><FloatingLabel controlId="floatingInput" label="Street"><Form.Control placeholder="Street" value={form.address?.street || ""} onChange={e => setForm({ ...form, address: { ...form.address, street: e.target.value } })} /></FloatingLabel></Form.Group></Col>
                <Col xs={6} className="mb-3"><Form.Group><FloatingLabel controlId="floatingInput" label="Apt/Suite"><Form.Control placeholder="Apt/Suite" value={form.address?.apt || ""} onChange={e => setForm({ ...form, address: { ...form.address, apt: e.target.value } })} /></FloatingLabel></Form.Group></Col>
                <Col xs={6} className="mb-3"><Form.Group><FloatingLabel controlId="floatingInput" label="City"><Form.Control placeholder="City" value={form.address?.city || ""} onChange={e => setForm({ ...form, address: { ...form.address, city: e.target.value } })} /></FloatingLabel></Form.Group></Col>
                <Col xs={6} className="mb-3"><Form.Group><FloatingLabel controlId="floatingInput" label="State"><Form.Control placeholder="State" value={form.address?.state || ""} onChange={e => setForm({ ...form, address: { ...form.address, state: e.target.value } })} /></FloatingLabel></Form.Group></Col>
                <Col xs={6} className="mb-3"><Form.Group><FloatingLabel controlId="floatingInput" label="ZIP Code"><Form.Control placeholder="ZIP Code" value={form.address?.zip || ""} onChange={e => setForm({ ...form, address: { ...form.address, zip: e.target.value } })} /></FloatingLabel></Form.Group></Col>
                <Col xs={12} className="mb-3"><Form.Group><FloatingLabel controlId="floatingInput" label="Country"><Form.Select value={form.address?.country || "United States"} onChange={e => setForm({ ...form, address: { ...form.address, country: e.target.value } })}><option value="United States">United States</option><option value="Afghanistan">Afghanistan</option><option value="Albania">Albania</option><option value="Algeria">Algeria</option><option value="Andorra">Andorra</option><option value="Angola">Angola</option><option value="Antigua and Barbuda">Antigua and Barbuda</option><option value="Argentina">Argentina</option><option value="Armenia">Armenia</option><option value="Australia">Australia</option><option value="Austria">Austria</option><option value="Azerbaijan">Azerbaijan</option><option value="Bahamas">Bahamas</option><option value="Bahrain">Bahrain</option><option value="Bangladesh">Bangladesh</option><option value="Barbados">Barbados</option><option value="Belarus">Belarus</option><option value="Belgium">Belgium</option><option value="Belize">Belize</option><option value="Benin">Benin</option><option value="Bhutan">Bhutan</option><option value="Bolivia">Bolivia</option><option value="Bosnia and Herzegovina">Bosnia and Herzegovina</option><option value="Botswana">Botswana</option><option value="Brazil">Brazil</option><option value="Brunei">Brunei</option><option value="Bulgaria">Bulgaria</option><option value="Burkina Faso">Burkina Faso</option><option value="Burundi">Burundi</option><option value="Cabo Verde">Cabo Verde</option><option value="Cambodia">Cambodia</option><option value="Cameroon">Cameroon</option><option value="Canada">Canada</option><option value="Central African Republic">Central African Republic</option><option value="Chad">Chad</option><option value="Chile">Chile</option><option value="China">China</option><option value="Colombia">Colombia</option><option value="Comoros">Comoros</option><option value="Congo">Congo</option><option value="Costa Rica">Costa Rica</option><option value="Croatia">Croatia</option><option value="Cuba">Cuba</option><option value="Cyprus">Cyprus</option><option value="Czech Republic">Czech Republic</option><option value="Democratic Republic of the Congo">Democratic Republic of the Congo</option><option value="Denmark">Denmark</option><option value="Djibouti">Djibouti</option><option value="Dominica">Dominica</option><option value="Dominican Republic">Dominican Republic</option><option value="East Timor">East Timor</option><option value="Ecuador">Ecuador</option><option value="Egypt">Egypt</option><option value="El Salvador">El Salvador</option><option value="Equatorial Guinea">Equatorial Guinea</option><option value="Eritrea">Eritrea</option><option value="Estonia">Estonia</option><option value="Eswatini">Eswatini</option><option value="Ethiopia">Ethiopia</option><option value="Fiji">Fiji</option><option value="Finland">Finland</option><option value="France">France</option><option value="Gabon">Gabon</option><option value="Gambia">Gambia</option><option value="Georgia">Georgia</option><option value="Germany">Germany</option><option value="Ghana">Ghana</option><option value="Greece">Greece</option><option value="Grenada">Grenada</option><option value="Guatemala">Guatemala</option><option value="Guinea">Guinea</option><option value="Guinea-Bissau">Guinea-Bissau</option><option value="Guyana">Guyana</option><option value="Haiti">Haiti</option><option value="Honduras">Honduras</option><option value="Hungary">Hungary</option><option value="Iceland">Iceland</option><option value="India">India</option><option value="Indonesia">Indonesia</option><option value="Iran">Iran</option><option value="Iraq">Iraq</option><option value="Ireland">Ireland</option><option value="Israel">Israel</option><option value="Italy">Italy</option><option value="Ivory Coast">Ivory Coast</option><option value="Jamaica">Jamaica</option><option value="Japan">Japan</option><option value="Jordan">Jordan</option><option value="Kazakhstan">Kazakhstan</option><option value="Kenya">Kenya</option><option value="Kiribati">Kiribati</option><option value="Kuwait">Kuwait</option><option value="Kyrgyzstan">Kyrgyzstan</option><option value="Laos">Laos</option><option value="Latvia">Latvia</option><option value="Lebanon">Lebanon</option><option value="Lesotho">Lesotho</option><option value="Liberia">Liberia</option><option value="Libya">Libya</option><option value="Liechtenstein">Liechtenstein</option><option value="Lithuania">Lithuania</option><option value="Luxembourg">Luxembourg</option><option value="Madagascar">Madagascar</option><option value="Malawi">Malawi</option><option value="Malaysia">Malaysia</option><option value="Maldives">Maldives</option><option value="Mali">Mali</option><option value="Malta">Malta</option><option value="Marshall Islands">Marshall Islands</option><option value="Mauritania">Mauritania</option><option value="Mauritius">Mauritius</option><option value="Mexico">Mexico</option><option value="Micronesia">Micronesia</option><option value="Moldova">Moldova</option><option value="Monaco">Monaco</option><option value="Mongolia">Mongolia</option><option value="Montenegro">Montenegro</option><option value="Morocco">Morocco</option><option value="Mozambique">Mozambique</option><option value="Myanmar">Myanmar</option><option value="Namibia">Namibia</option><option value="Nauru">Nauru</option><option value="Nepal">Nepal</option><option value="Netherlands">Netherlands</option><option value="New Zealand">New Zealand</option><option value="Nicaragua">Nicaragua</option><option value="Niger">Niger</option><option value="Nigeria">Nigeria</option><option value="North Korea">North Korea</option><option value="North Macedonia">North Macedonia</option><option value="Norway">Norway</option><option value="Oman">Oman</option><option value="Pakistan">Pakistan</option><option value="Palau">Palau</option><option value="Panama">Panama</option><option value="Papua New Guinea">Papua New Guinea</option><option value="Paraguay">Paraguay</option><option value="Peru">Peru</option><option value="Philippines">Philippines</option><option value="Poland">Poland</option><option value="Portugal">Portugal</option><option value="Qatar">Qatar</option><option value="Romania">Romania</option><option value="Russia">Russia</option><option value="Rwanda">Rwanda</option><option value="Saint Kitts and Nevis">Saint Kitts and Nevis</option><option value="Saint Lucia">Saint Lucia</option><option value="Saint Vincent and the Grenadines">Saint Vincent and the Grenadines</option><option value="Samoa">Samoa</option><option value="San Marino">San Marino</option><option value="Sao Tome and Principe">Sao Tome and Principe</option><option value="Saudi Arabia">Saudi Arabia</option><option value="Senegal">Senegal</option><option value="Serbia">Serbia</option><option value="Seychelles">Seychelles</option><option value="Sierra Leone">Sierra Leone</option><option value="Singapore">Singapore</option><option value="Slovakia">Slovakia</option><option value="Slovenia">Slovenia</option><option value="Solomon Islands">Solomon Islands</option><option value="Somalia">Somalia</option><option value="South Africa">South Africa</option><option value="South Korea">South Korea</option><option value="South Sudan">South Sudan</option><option value="Spain">Spain</option><option value="Sri Lanka">Sri Lanka</option><option value="Sudan">Sudan</option><option value="Suriname">Suriname</option><option value="Sweden">Sweden</option><option value="Switzerland">Switzerland</option><option value="Syria">Syria</option><option value="Taiwan">Taiwan</option><option value="Tajikistan">Tajikistan</option><option value="Tanzania">Tanzania</option><option value="Thailand">Thailand</option><option value="Togo">Togo</option><option value="Tonga">Tonga</option><option value="Trinidad and Tobago">Trinidad and Tobago</option><option value="Tunisia">Tunisia</option><option value="Turkey">Turkey</option><option value="Turkmenistan">Turkmenistan</option><option value="Tuvalu">Tuvalu</option><option value="Uganda">Uganda</option><option value="Ukraine">Ukraine</option><option value="United Arab Emirates">United Arab Emirates</option><option value="United Kingdom">United Kingdom</option><option value="Uruguay">Uruguay</option><option value="Uzbekistan">Uzbekistan</option><option value="Vanuatu">Vanuatu</option><option value="Vatican City">Vatican City</option><option value="Venezuela">Venezuela</option><option value="Vietnam">Vietnam</option><option value="Yemen">Yemen</option><option value="Zambia">Zambia</option><option value="Zimbabwe">Zimbabwe</option></Form.Select></FloatingLabel></Form.Group></Col>
              </Row>
            </Form>
          </RBModal.Body>
          <RBModal.Footer className="px-4 border-top-0">
            <Button variant="light" onClick={() => setIsAddressModalOpen(false)}>
              Cancel
            </Button>
            <Button variant="primary" onClick={() => { setIsAddressModalOpen(false); handleSaveProfile({ address: form.address }); }}>
              Save Changes
            </Button>
          </RBModal.Footer>
        </RBModal>

        {/* Work Information Modal */}
        <RBModal show={isWorkModalOpen} onHide={() => setIsWorkModalOpen(false)} centered>
          <RBModal.Header closeButton className="border-bottom-0 px-4">
            <RBModal.Title className="w-100"> 
              <div className="d-flex flex-column justify-content-start align-items-start">
                <span>Edit Work Information</span>
                <div className="text-hl"></div>
              </div>
            </RBModal.Title>
          </RBModal.Header>
          <RBModal.Body className="px-4">
            <Form>
              <Row className="mb-2">
                <Col><Form.Group className="mb-3">
                  <FloatingLabel controlId="floatingInput" label="Department">
                    <Form.Control placeholder="Department" value={form.department || ""} onChange={e => setForm({ ...form, department: e.target.value })} />
                  </FloatingLabel>
                </Form.Group></Col>
                <Col><Form.Group className="mb-3">
                  <FloatingLabel controlId="floatingInput" label="Position Title">
                    <Form.Control placeholder="Position Title" value={form.positionTitle || ""} onChange={e => setForm({ ...form, positionTitle: e.target.value })} />
                  </FloatingLabel>
                </Form.Group></Col>
              </Row>
              <Row className="mb-2">
                <Col><Form.Group className="mb-3">
                  <FloatingLabel controlId="floatingInput" label="License Number">
                    <Form.Control placeholder="License Number" value={form.licenseNumber || ""} onChange={e => setForm({ ...form, licenseNumber: e.target.value })} />
                  </FloatingLabel>
                </Form.Group></Col>
                <Col><Form.Group className="mb-3">
                  <FloatingLabel controlId="floatingInput" label="NPI Number">
                    <Form.Control placeholder="NPI Number" value={form.npiNumber || ""} onChange={e => setForm({ ...form, npiNumber: e.target.value })} />
                  </FloatingLabel>
                </Form.Group></Col>
              </Row>
              <Row className="mb-2">
                <Col><Form.Group className="mb-3">
                  <FloatingLabel controlId="floatingInput" label="Board Certified">
                    <Form.Select value={form.boardCertified ? "Yes" : "No"} onChange={e => setForm({ ...form, boardCertified: e.target.value === "Yes" })}>
                    <option value="No">No</option>
                    <option value="Yes">Yes</option>
                  </Form.Select>
                  </FloatingLabel>
                </Form.Group></Col>
                <Col><Form.Group className="mb-3">
                  <FloatingLabel controlId="floatingInput" label="Start Date">
                    <Form.Control type="date" placeholder="Start Date" value={form.startDate || ""} onChange={e => setForm({ ...form, startDate: e.target.value })} />
                  </FloatingLabel>
                </Form.Group></Col>
                <Col><Form.Group className="mb-3">
                  <FloatingLabel controlId="floatingInput" label="End Date">
                    <Form.Control type="date" placeholder="End Date" value={form.endDate || ""} onChange={e => setForm({ ...form, endDate: e.target.value })} />
                  </FloatingLabel>
                </Form.Group></Col>
              </Row>
            </Form>
          </RBModal.Body>
          <RBModal.Footer className="px-4 border-top-0">
            <Button variant="light" onClick={() => setIsWorkModalOpen(false)}>
              Cancel
            </Button>
            <Button variant="primary" onClick={() => { setIsWorkModalOpen(false); handleSaveProfile({
              department: form.department,
              positionTitle: form.positionTitle,
              licenseNumber: form.licenseNumber,
              npiNumber: form.npiNumber,
              boardCertified: form.boardCertified,
              startDate: form.startDate,
              endDate: form.endDate
            }); }}>
              Save Changes
            </Button>
          </RBModal.Footer>
        </RBModal>

      {/* Change Password Modal */}
        <RBModal show={isPasswordModalOpen} onHide={() => setIsPasswordModalOpen(false)} centered>
          <RBModal.Header closeButton className="border-bottom-0 px-4">
            <RBModal.Title className="w-100"> 
              <div className="d-flex flex-column justify-content-start align-items-start">
                <span>Change Password</span>
                <div className="text-hl"></div>
              </div>
            </RBModal.Title>
          </RBModal.Header>
          <RBModal.Body className="px-4">
            <Form>
              <Form.Group className="mb-2">
                <FloatingLabel controlId="floatingInput" label="Current Password">
                <Form.Control
            type="password"
            placeholder="Current Password"
            value={currentPassword}
                  onChange={e => setCurrentPassword(e.target.value)}
          />
                </FloatingLabel>
              </Form.Group>
              <Form.Group >
                <FloatingLabel controlId="floatingInput" label="New Password">
                <Form.Control
            type="password"
            placeholder="New Password"
            value={newPassword}
                  onChange={e => setNewPassword(e.target.value)}
                />
                </FloatingLabel>
              </Form.Group>
            </Form>
          </RBModal.Body>
          <RBModal.Footer className="px-4 border-top-0">
            <Button variant="light" size="sm" onClick={() => setIsPasswordModalOpen(false)}>
              Cancel
            </Button>
            <Button variant="primary" size="sm" onClick={handleChangePassword}>
              Change Password
            </Button>
          </RBModal.Footer>
        </RBModal>
      </Container>
    </div>
  );
};

export default ProfilePage;
